import React, { useEffect, useState } from "react";
import styled from "styled-components";

// #region  SCOPED CSS
const StyledElem = styled.div`
    .control-description {
        color: var(--text-icons-gray-100);
        font-size: 12px;
    }

    .switch {
        display: flex;
        align-self: center;
        height: 17px;
        margin: 0;
        position: relative;
        width: 45px;
    }

    .switch input {
        display: none;
    }

    .slider.round {
        border-radius: 34px;
        outline: none;
    }

    .slider {
        background-color: var(--backgrounds-lines-n-600);
        bottom: 0;
        cursor: pointer;
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
        transition: 0.4s;
    }

    .slider.round:before {
        border-radius: 50%;
    }

    .slider:before {
        background-color: var(--text-icons-gray-300);
        border: 5px solid var(--backgrounds-lines-n-400);
        bottom: -1px;
        content: "";
        height: 22px;
        left: 0;
        position: absolute;
        transition: 0.4s;
        width: 22px;
        filter: drop-shadow(0px 0px 10px var(--backgrounds-lines-n-600));
    }

    input:checked + .slider {
        background-color: var(--backgrounds-lines-n-600);
    }

    .slider.round {
        border-radius: 34px;
        outline: none;
    }

    input:checked + .slider.round:before {
        background-color: var(--text-icons-green-500) !important;
        -webkit-transform: translateX(25px);
        transform: translateX(25px);
    }

    .slider.round:before {
        border-radius: 50%;
    }

    .slider.round:before {
        background-color: var(--text-icons-orange-500) !important;
    }

    .switch.always-green .slider.round:before {
        background-color: var(--text-icons-green-500) !important;
    }

    .switch.disable .slider.round:before {
        background-color: var(--text-icons-gray-300) !important;
    }
`;
// #endregion

/** SwitchButton Component
 *
 * A customizable toggle switch button component with support for controlled and uncontrolled modes.
 *
 * @param {Object} props
 * @param {string} props.className - Additional CSS class name for the toggle container.
 * @param {Object} [props.style={}] - Additional inline styles for the toggle container.
 * @param {string} [props.height="19px"] - The height of the toggle.
 * @param {string} props.title - The label text displayed to the left of the toggle.
 * @param {string} props.rightTitle - The label text displayed to the right of the toggle.
 * @param {function} props.onToogleChanged - Callback function invoked when the toggle state changes. Receives the new toggle state (boolean) as an argument.
 * @param {boolean} [props.value] - Controls the toggle state. When provided, the component operates in controlled mode and will sync with this value. When undefined, the component operates in uncontrolled mode with internal state management (defaults to false).
 * @param {boolean} props.isDisabled - When true, disables the toggle and applies disabled styling.
 * @param {boolean} [props.alwaysGreen=false] - When true, the toggle will always display in green color regardless of state.
 *
 * @returns {React.Component} - The SwitchButton component.
 * @example
 * // Controlled usage - toggle state managed by parent component
 * <SwitchButton
 *   title="Enable Feature"
 *   value={featureEnabled}
 *   onToogleChanged={(newState) => setFeatureEnabled(newState)}
 * />
 *
 * @example
 * // Uncontrolled usage - component manages its own state
 * <SwitchButton
 *   title="All Brands"
 *   onToogleChanged={(newState) => console.log('Toggled:', newState)}
 * />
 */
export default function SwitchButton(props) {
    const {
        className = "",
        style = {},
        height = "19px",
        title,
        rightTitle,
        onToogleChanged,
        value,
        isDisabled,
        alwaysGreen = false
    } = props;

    // #region STATES
    const [isToggled, setIsToggled] = useState(false);
    // #endregion

    // #region METHODS
    const handleToggle = () => {
        // In controlled mode (value is provided), don't update internal state
        // Let the parent component control the state via the value prop
        if (value === undefined) {
            setIsToggled(!isToggled);
        }
        onToogleChanged && onToogleChanged(!isToggled);
    };
    // #endregion

    // #region EFFECTS

    useEffect(() => {
        if (value !== undefined) {
            setIsToggled(value);
        }
    }, [value]); // Sync with value prop if provided (controlled mode)
    // #endregion

    return (
        <StyledElem
            className={`switch-button flex gap-8px ${isDisabled ? "disabled" : ""} ${className}`}
            style={{ height, ...style }}>
            {title && <span className="control-description">{title}</span>}

            <label className={`switch ${isDisabled ? "disable" : ""} ${alwaysGreen ? "always-green" : ""}`}>
                <input type="checkbox" disabled={isDisabled} checked={isToggled} onChange={handleToggle} />
                <span className="slider round"></span>
            </label>

            {rightTitle && <span className="control-description">{rightTitle}</span>}
        </StyledElem>
    );
}
